import { LitElement, html, nothing, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { BackendFormResponse, Producer } from '../util/api';
import { consume } from '@lit/context';
import { breakpointContext } from '../contexts/breakpoint';
import { BREAKPOINT } from '../util/breakpoints';
import { makeCustomEvent, EVENT } from '../util/events';
import { formatCurrency } from '../util/format';
import { subsidySavingsTooltip } from '../util/tooltips';
import { inputResponseContext } from '../contexts/inputResponse';
import { filtersContext } from '../contexts/filters';
import { Filters } from '../util/filter-sort';
import { getAnalyticPageDescription } from '../util/analytics';
import { SOURCE } from '../util/source';
import { sourceContext } from '../contexts/source';
import { agentContext } from '../contexts/agent';

@customElement('rq-subsidy-banner')
export class SubsidyBanner extends LitElement {
    static styles = css`
        .subsidy-wrapper {
            width: 80%;
        }

        .subsidy-wrapper-mobile {
            width: 72%;
        }

        .savings-header,
        .subsidy-savings {
            display: flex;
            flex-direction: row;
            gap: 16px;
            align-items: center;
        }

        .subsidy-savings {
            gap: 8px;
        }
        .banner-buttons {
            gap: 8px;
            display: flex;
            flex-direction: row;
        }

        .text-color {
            color: var(--mode-typography-text, #222425);
        }
        .link {
            cursor: pointer;
        }
        .contact-link-right {
            display: flex;
            justify-content: flex-end;
        }
    `;

    @consume({ context: inputResponseContext, subscribe: true })
    @state()
    inputResponse: BackendFormResponse = {
        subsidy: { subsidyAmt: '', houseHoldIncome: '', familySize: '', subsidyEligible: false, csrLevel: 1 },
        context: '',
        applicants: [],
        plans: [],
        referralSource: '',
        todaysTime: '',
        todaysDate: '',
        nextEffectiveDate: '',
        effectiveStart: '',
        effectiveEnd: '',
        quoteDate: '',
        zipCode: '',
        county: '',
        regionCode: '',
        hasDental: false,
        hasMaternity: false,
    };

    @consume({ context: breakpointContext, subscribe: true })
    breakpoint!: BREAKPOINT;

    @consume({ context: filtersContext, subscribe: true })
    filters: Filters = {
        // topPick: false,
        marketplace: null,
        productType: [],
        metallicLevel: [],
        easyPricing: false,
    };

    @consume({ context: sourceContext, subscribe: true })
    source!: SOURCE;

    @consume({ context: agentContext, subscribe: true })
    agent?: Producer;

    get isMobile() {
        return this.breakpoint === BREAKPOINT.PHONE || this.breakpoint === BREAKPOINT.TABLET;
    }
    get isShopper() {
        return this.inputResponse?.context?.toLowerCase() === 'shopper';
    }
    get isQuotedSale() {
        return this.inputResponse?.referralSource === 'QUOTED_SALE';
    }

    get amountLabel() {
        if (this.inputResponse?.subsidy?.subsidyAmt && Number(this.inputResponse?.subsidy?.subsidyAmt) > 0) {
            return html`<lh-typography type="heading" selector="h6" size="xl"
                    >${formatCurrency(this.inputResponse?.subsidy.subsidyAmt)}/mo</lh-typography
                >
                <span>
                    <lh-typography size="xxs">
                        <lh-tooltip description=${subsidySavingsTooltip}>in estimated subsidy savings</lh-tooltip>
                    </lh-typography>
                </span>`;
        } else {
            return html`<lh-typography
                >Your ${this.isShopper ? nothing : html`client's`} income of
                ${formatCurrency(this.inputResponse.subsidy?.houseHoldIncome)} for a family of
                ${this.inputResponse.subsidy?.familySize} likely does not qualify for a subsidy.</lh-typography
            >`;
        }
    }

    get medicaidDisclaimer() {
        if (this.inputResponse?.subsidy?.subsidyEligible !== false || !this.inputResponse?.subsidy) return nothing;
        return html`<lh-typography
            >Based on the information provided, ${this.isShopper ? 'you do not' : 'your client does not'} appear to be
            eligible for a subsidy.<br />
            Instead, ${this.isShopper ? 'you' : 'they'} may be eligible for Medicaid. Visit
            <a class="text-color" href="https://HealthCare.gov" target="_blank"
                >HealthCare.gov<lh-icon size="sm" name="arrow-square-out" class="pdfColor"></lh-icon
            ></a>
            to find out if ${this.isShopper ? 'you' : 'your client'} may qualify for Medicaid coverage.</lh-typography
        >`;
    }

    get hideEditButton() {
        return Boolean(this.source === SOURCE.SALESFORCE && this.agent);
    }

    get bannerContent() {
        if (this.inputResponse.subsidy) {
            return html` <div class="savings-header">
                    <div class="subsidy-savings">${this.amountLabel}</div>
                    ${this.hideEditButton
                        ? nothing
                        : html`<span>
                              <lh-typography size="sm">
                                  <lh-button
                                      variant="text"
                                      analytics-id="rate-quote:${this.source}:u65:plans-${getAnalyticPageDescription(
                                          this.filters
                                      )}:edit-subsidy:button-text"
                                      @click=${() => {
                                          this.dispatchEvent(makeCustomEvent(EVENT.EDIT_INFO));
                                      }}
                                  >
                                      Edit${this.isMobile ? '' : ' info'}
                                  </lh-button>
                              </lh-typography>
                          </span>`}
                </div>
                ${this.medicaidDisclaimer}`;
        } else {
            return html`<div class="savings-header">
                <lh-icon name="calculator" size="lg"></lh-icon>
                <div>
                    <lh-typography>
                        <span class="banner-buttons">
                            <lh-typography size=${this.isMobile ? 'xs' : 'sm'}
                                >${!this.isMobile && this.isShopper
                                    ? html`Are you`
                                    : !this.isMobile
                                      ? html`Is your client`
                                      : nothing}

                                <lh-button
                                    variant="text"
                                    class="link"
                                    analytics-id="rate-quote:${this.source}:u65:plans-${getAnalyticPageDescription(
                                        this.filters
                                    )}:edit-subsidy:button-text"
                                    @click=${() => {
                                        this.dispatchEvent(makeCustomEvent(EVENT.EDIT_INFO, { subsidy: true }));
                                    }}
                                >
                                    eligible for a subsidy?
                                </lh-button>
                            </lh-typography>
                            <lh-button
                                variant="text"
                                analytics-id="rate-quote:${this.source}:u65:plans-${getAnalyticPageDescription(
                                    this.filters
                                )}:edit-subsidy:button-text"
                                @click=${() => {
                                    this.dispatchEvent(makeCustomEvent(EVENT.EDIT_INFO));
                                }}
                            >
                                Edit${this.isMobile ? '' : ' info'}
                            </lh-button>
                        </span>
                    </lh-typography>
                </div>
            </div>`;
        }
    }

    get hideBanner() {
        return (this.hideEditButton && !this.inputResponse.subsidy) || this.source === SOURCE.CHANGE_APP;
    }

    render() {
        return this.hideBanner
            ? nothing
            : html`<lh-container
                  gutters=${this.isMobile ? 'sm' : 'md'}
                  theme="primary-light"
                  padding-top="sm"
                  padding-bottom="sm"
                  content-width="xxl"
              >
                 <div style="display:flex;justify-content:space-between;align-items:center;">
                  <div class=${classMap({ 'subsidy-wrapper': true, 'subsidy-wrapper-mobile': this.isMobile })}>${this.bannerContent}</div><div class="contact-link-right">
                   ${
                       !this.isShopper || this.isQuotedSale
                           ? nothing
                           : html`
                                 <lh-typography size=${this.isMobile ? 'xs' : 'sm'}>
                                     <lh-button
                                         variant="text"
                                         size="md"
                                         analytics-id="rate-quote:contact-us:button-text"
                                         href="https://www.bluecrossnc.com/contact-us/call#container-f0c9924471"
                                         target="_blank"
                                     >
                                         <lh-icon name="phone-call" size=${this.isMobile ? 'sm' : 'md'}></lh-icon>
                                         Contact Us
                                     </lh-button>
                                 </lh-typography>
                             `
                   }
                    </div>
                    </div>
                </div>
              </lh-container>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-subsidy-banner': SubsidyBanner;
    }
}
